import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, DatePicker, Select, Space, Table, Tooltip } from 'antd';
import { DownloadOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';

import { useAccess } from '../../../auth/AccessContext.jsx';
import ActionIcon from '../../../components/ActionIcon.jsx';
import { useBusinessDay } from '../../../businessDay/BusinessDayContext.js';
import ActionConfirmModal from './ActionConfirmModal.jsx';
import ApprovalFundFilter from './ApprovalFundFilter.jsx';
import { orderColumns, reviewByColumn, reviewsColumn } from './orderColumns.jsx';
import { approveAction, approvesFromPending, pauseSentence, rejectAction, reviewAction } from './orderRows.js';
import { OPEN_STATES, STATE_LABEL, TRANSACTION_TYPE_FILTERS, blockedDates, dealingDatesOf, pendingOrdersQuery, summarise, withPartners } from './orders.js';
import { failureText, outcomeOf } from './redemptionNotes.js';
import ReviewDialog from './ReviewDialog.jsx';
import { useOrderList, usePricingGuard } from './useOrders.js';

/** How often the page reads its own clock again, so a Review by deadline turns red without a reload (no API call). */
const CLOCK_MS = 30 * 1000;

/**
 * These orders and the other leg of every switch or transfer among them -
 * from the rows already loaded, or from GET /orders/{id} when a filter has
 * hidden it. A pair is reviewed, approved and rejected whole (G-4, design
 * 2.1): the server skips a lone leg (APR-011).
 *
 * @param {object} api
 * @param {object[]} orders
 * @param {object[]} loaded the rows on the page
 */
async function withLegs(api, orders, loaded) {
    const rows = [...loaded, ...orders.filter((o) => !loaded.some((l) => l.orderId === o.orderId))];
    const found = withPartners(orders.map((o) => o.orderId), rows).orders;
    const missing = [...new Set(found.map((o) => o.linkedOrderId).filter((id) => id && !found.some((o) => o.orderId === id)))];
    const fetched = await Promise.all(missing.map(async (orderId) => (await api.orders.detail(orderId)).order));
    return [...found, ...fetched.filter(Boolean)];
}

/**
 * The pending orders' table (design 8.2): every open order with its
 * provisional figures, its review deadline and reviews, and the buttons each
 * row offers - Review (or Review Again) and Reject, and Approve on a fund
 * transfer's row only (orderRows.js approvesFromPending; every other order is
 * approved from Ready for approval) - each off with the sentence that says why
 * (orderRows.js). Rendered on its own by the screen tests, with fixture rows.
 *
 * @param {{ orders: object[], loading?: boolean, onOpen?: (order) => void, blocked?: Map<string, string[]>, nowMs?: number,
 *   onReview?: (order) => void, onApprove?: (order) => void,
 *   onReject?: (order) => void }} props
 */
export const PendingOrdersTable = ({
    orders, loading = false, onOpen, blocked, nowMs = Date.now(),
    onReview = () => {}, onApprove = () => {}, onReject = () => {}
}) => {
    const { canAct } = useAccess();
    const { reviewPaused } = useBusinessDay();
    const canReview = canAct('approval', 'review');
    const canApprove = canAct('approval', 'approve');
    const canReject = canAct('approval', 'reject');

    const base = orderColumns({ onOpen, blocked });
    const columns = [
        ...base.filter((c) => c.key !== 'receivedAt'),
        reviewByColumn(nowMs),
        reviewsColumn(),
        ...base.filter((c) => c.key === 'receivedAt'),
        {
            title: 'Review',
            key: 'review',
            width: 130,
            fixed: 'right',
            render: (_, order) => {
                const review = reviewAction(order, { canReview, reviewPaused });
                return (
                    <Tooltip title={review.reason}>
                        <span>
                            <Button className="review-action-button" size="small" disabled={!review.enabled} onClick={() => onReview(order)}>{review.label}</Button>
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 170,
            fixed: 'right',
            render: (_, order) => {
                const approve = approveAction(order, { canApprove, blockedFunds: blocked?.get(order.dealingDate) ?? null });
                const reject = rejectAction(order, { canReject });
                return (
                    <Space size={4}>
                        {approvesFromPending(order) && (
                            <Tooltip title={approve.reason}>
                                <span>
                                    <Button size="small" type="primary" disabled={!approve.enabled} onClick={() => onApprove(order)}>Approve</Button>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title={reject.reason}>
                            <span>
                                <Button size="small" danger disabled={!reject.enabled} onClick={() => onReject(order)}>Reject</Button>
                            </span>
                        </Tooltip>
                    </Space>
                );
            }
        }
    ];

    return (
        <Table
            className="pending-transactions-table approval-orders-table"
            rowKey="orderId"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={loading && orders.length === 0 ? undefined : orders}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            locale={{ emptyText: 'Nothing pending - transactions appear here as soon as LIV reports a submission' }}
            scroll={{ x: 'max-content' }}
        />
    );
};

/**
 * Every order still open in the Core (IP-89; design spec 9.2, design
 * 2026-10-06-order-review-business-day-design.md 8.2): from the client's
 * completed instruction through funding, clearance, book close and the price
 * gate to READY, and anything on hold. IP-177 (LWM1-476): an instruction the
 * client has not finished in LIV - drafts, and joint, corporate and trust
 * requests still collecting signatures - is not here: the server leaves it
 * out until LIV completes it.
 *
 * Review is mandatory (IP-125): an order not reviewed by its book's cut-off
 * rolls to the next dealing day (IP-126), so Review is offered in every
 * reviewable state and the rows say when each must be reviewed by. Several
 * people may review an order (IP-127); a switch's or transfer's other leg is
 * added when one is reviewed. A checker approves - someone who reviewed the
 * order in no cycle (IP-128) - or rejects, from any open state (IP-130).
 * While the business day's Repricing or Day end step runs, reviews of the
 * orders it covers are paused (IP-129): their Review is greyed with the
 * server's sentence. Every action reads the business day again when it
 * finishes.
 *
 * @param {{ refreshToken: number, onOpen: (order) => void, onCount?: (count: number, more: boolean) => void, onChanged?: () => void }} props
 */
const PendingTransactions = ({ refreshToken, onOpen, onCount, onChanged }) => {
    const { api, canAct } = useAccess();
    const { message } = App.useApp();
    const canDownload = canAct('approval', 'generateDocument');
    const [downloading, setDownloading] = useState(false);
    const { reviewPaused, refresh: refreshDay } = useBusinessDay();
    const [states, setStates] = useState([]);
    const [txnType, setTxnType] = useState(null);
    const [fundId, setFundId] = useState(null);
    const [dealingDate, setDealingDate] = useState(null);
    const [reviewing, setReviewing] = useState(null); // { orders, primary }
    const [confirming, setConfirming] = useState(null); // { kind: 'approve'|'reject', orders }
    const [outcome, setOutcome] = useState(null);
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNowMs(Date.now()), CLOCK_MS);
        return () => clearInterval(timer);
    }, []);

    const downloadNotes = async () => {
        setDownloading(true);
        try {
            const result = outcomeOf(await api.cash.redemptionNotes(['docx']));
            if (result.kind === 'empty') message.info('No pending redemptions');
            if (result.kind === 'download') {
                const link = document.createElement('a');
                link.href = result.url;
                link.download = result.fileName;
                link.click();
            }
            if (result.failed.length) message.warning({ content: failureText(result.failed), duration: 0 });
        } catch (e) {
            message.error(e.message);
        } finally {
            setDownloading(false);
        }
    };

    const query = useMemo(() => pendingOrdersQuery({
        states,
        txnType,
        fundId,
        dealingDate: dealingDate ? dealingDate.format('YYYY-MM-DD') : null
    }), [states, txnType, fundId, dealingDate]);
    const list = useOrderList(api, query);
    const { reload } = list;
    const dates = useMemo(() => dealingDatesOf(list.orders), [list.orders]);
    const guard = usePricingGuard(api, dates, refreshToken);
    const blocked = useMemo(() => blockedDates(guard.entries), [guard.entries]);

    useEffect(() => {
        if (refreshToken) reload();
    }, [refreshToken, reload]);

    // The tab's badge counts what the unfiltered view holds.
    const unfiltered = !states.length && !txnType && !fundId && !dealingDate;
    useEffect(() => {
        if (unfiltered && !list.loading && !list.error) onCount?.(list.orders.length, Boolean(list.nextCursor));
    }, [unfiltered, list.loading, list.error, list.orders.length, list.nextCursor, onCount]);

    const paused = pauseSentence(reviewPaused);

    const finish = (kind, result) => {
        const known = [...list.orders, ...(confirming?.orders ?? []), ...(reviewing?.orders ?? [])];
        const refOf = (orderId) => known.find((o) => o.orderId === orderId)?.transactionRef ?? orderId;
        const summary = summarise(kind, result, refOf);
        if (summary.type === 'success') message.success(summary.title);
        setOutcome(summary.type === 'success' ? null : summary);
        setReviewing(null);
        setConfirming(null);
        // An order action can move the business day (a reject, an approval, a review in time): read it again.
        refreshDay();
        if (onChanged) onChanged();
        else reload();
    };

    /** Gather an action's legs, then open its dialog; a failed read of a hidden partner is said, not swallowed. */
    const gather = async (orders, open) => {
        try {
            open(await withLegs(api, orders, list.orders));
        } catch (e) {
            message.error(e.message);
        }
    };

    const reviewOne = (order) => gather([order], (orders) => setReviewing({ orders, primary: order }));
    const approveOne = (order) => gather([order], (orders) => setConfirming({ kind: 'approve', orders }));
    const rejectOne = (order) => setConfirming({ kind: 'reject', orders: [order] });

    return (
        <section className="panel pending-transactions-panel">
            <div className="pending-transactions-toolbar">
                <h3 className="panel-title">Pending transactions</h3>
                <Space className="pending-transactions-actions" size={8} wrap>
                    {canDownload && (
                        <Button icon={<DownloadOutlined />} onClick={downloadNotes} loading={downloading}>Download redemption notes</Button>
                    )}
                    <Button className="refresh-button" icon={<ActionIcon name="refresh" />} onClick={reload} loading={list.loading}>Refresh</Button>
                </Space>
            </div>
            <div className="pending-transactions-info">
                <ActionIcon name="info" />
                <span>
                    {list.orders.length}{list.nextCursor ? '+' : ''} open transaction(s).
                    Every transaction is reviewed before its book&apos;s cut-off, or it rolls to the next dealing day.
                    Units are allocated only when a transaction is approved.
                </span>
            </div>

            <div className="pending-transactions-filters">
                <Select
                    className="pending-type-filter"
                    allowClear
                    aria-label="Transaction Type"
                    placeholder="All Types"
                    value={txnType ?? undefined}
                    onChange={(value) => setTxnType(value ?? null)}
                    options={TRANSACTION_TYPE_FILTERS.map(({ value, label }) => ({ value, label }))}
                />
                <Select
                    className="pending-state-filter"
                    mode="multiple"
                    allowClear
                    placeholder="All State"
                    value={states}
                    onChange={setStates}
                    options={OPEN_STATES.map((s) => ({ value: s, label: STATE_LABEL[s] }))}
                    maxTagCount="responsive"
                />
                <ApprovalFundFilter className="pending-fund-filter" fundId={fundId} onChange={setFundId}
                    seen={list.orders.map((o) => o.fundId)} />
                <DatePicker className="pending-date-filter" placeholder="Effective Date" format="YYYY-MM-DD"
                    value={dealingDate} onChange={setDealingDate} />
            </div>

            {paused && <Alert type="warning" showIcon className="auth-alert" title={paused} />}
            {outcome && (
                <Alert
                    type={outcome.type}
                    showIcon
                    closable={{ onClose: () => setOutcome(null) }}
                    className="auth-alert"
                    title={outcome.title}
                    description={outcome.lines.length ? outcome.lines.map((line, i) => <div key={i}>{line}</div>) : undefined}
                />
            )}
            {list.error && <Alert type="error" showIcon title={list.error} className="auth-alert" />}
            {guard.error && <Alert type="error" showIcon title={guard.error} className="auth-alert" />}

            <PendingOrdersTable
                orders={list.orders}
                loading={list.loading}
                onOpen={onOpen}
                blocked={blocked}
                nowMs={nowMs}
                onReview={reviewOne}
                onApprove={approveOne}
                onReject={rejectOne}
            />
            {list.nextCursor && (
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                    <Button onClick={list.loadMore} loading={list.loading}>Load more</Button>
                </div>
            )}

            <ReviewDialog
                orders={reviewing?.orders ?? []}
                primary={reviewing?.primary ?? null}
                open={Boolean(reviewing)}
                onClose={() => setReviewing(null)}
                onDone={(result) => finish('review', result)}
            />
            <ActionConfirmModal
                kind={confirming?.kind ?? null}
                orders={confirming?.orders ?? []}
                onClose={() => setConfirming(null)}
                onDone={finish}
            />
        </section>
    );
};

export default PendingTransactions;
